import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Link,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Language, WarningAmber } from '@mui/icons-material';

import {
  IKiInternetFreigabeProposal,
  IKiInternetVerwendung,
} from '../Interfaces/IKi';
import { customFetcher } from '../hooks/useFetch';

/**
 * Kennzeichnung an einer Antwort: hierfür wurde das Internet benutzt (#1336,
 * Vorgabe 15.10.2026). Steht **über** dem Text, damit man es vor dem Lesen
 * sieht, und nennt die Quellen.
 */
export const KiInternetHinweis: React.FC<{
  verwendung: IKiInternetVerwendung[];
}> = ({ verwendung }) => {
  const quellen = verwendung
    .flatMap(v => v.Quellen)
    .filter((q, i, alle) => alle.findIndex(x => x.url === q.url) === i);
  const mitMandanten = verwendung.some(v => v.MitMandantendaten);
  const suchdienst = Array.from(
    new Set(verwendung.map(v => v.Suchdienst).filter(Boolean))
  ).join(', ');
  const ungeprueft = verwendung.some(v => v.QuellenUngeprueft);

  return (
    <Box
      sx={{
        mb: 1,
        px: 1,
        py: 0.5,
        borderLeft: 3,
        borderColor: 'info.main',
        bgcolor: 'background.paper',
        borderRadius: 1,
      }}
    >
      <Stack direction='row' spacing={0.5} alignItems='center'>
        <Language fontSize='small' color='info' />
        <Typography variant='caption' sx={{ fontWeight: 600 }}>
          Internet verwendet{suchdienst ? ` — Suche über ${suchdienst}` : ''}
          {mitMandanten ? ' — mit freigegebenen Mandantendaten' : ''}
        </Typography>
      </Stack>
      <Typography variant='caption' color='text.secondary' component='div'>
        Gesucht: {verwendung.map(v => `„${v.Anfrage}"`).join(', ')}
      </Typography>
      {ungeprueft && (
        <Typography variant='caption' color='warning.main' component='div'>
          Quellen vom Modell genannt, nicht als Suchtreffer geliefert —
          ungeprüft.
        </Typography>
      )}
      {quellen.length > 0 && (
        <Stack component='ul' sx={{ m: 0, pl: 2 }}>
          {quellen.slice(0, 8).map(q => (
            <Typography component='li' variant='caption' key={q.url}>
              <Link href={q.url} target='_blank' rel='noopener noreferrer'>
                {q.titel || q.url}
              </Link>
            </Typography>
          ))}
        </Stack>
      )}
    </Box>
  );
};

/**
 * Rückfrage vor **jeder** Internetsuche (#1336, Entscheidung 15.10.2026).
 *
 * Gesucht ist noch **nichts** — DiNo hat die Anfrage angehalten. Der Klick gibt
 * genau diese eine Anfrage frei (einmal, 10 Minuten); die Bestätigung geht als
 * Nachricht an Karl, der dann sucht. Mit Mandantendaten als Warnung.
 */
export const KiInternetFreigabeCard: React.FC<{
  proposal: IKiInternetFreigabeProposal;
  onFreigegeben: (_anfrage: string) => void;
}> = ({ proposal, onFreigegeben }) => {
  const [state, setState] = useState<'offen' | 'freigegeben' | 'abgelehnt'>(
    'offen'
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { Display, Payload } = proposal;
  const mitMandanten = Display.Mandanten.length > 0;
  const suchdienst = Display.Suchdienst ?? 'die Websuche des KI-Anbieters';

  if (state === 'abgelehnt') {
    return (
      <Alert severity='info'>
        Nicht gesucht — die Anfrage hat DiNo nicht verlassen.
      </Alert>
    );
  }

  if (state === 'freigegeben') {
    return <Alert severity='success'>Freigegeben — Karl sucht jetzt.</Alert>;
  }

  async function handleFreigabe() {
    setError(null);
    setIsLoading(true);
    try {
      await customFetcher({
        endpoint: '/ki/internet/freigabe',
        method: 'POST',
        body: { idFreigabe: Payload.idFreigabe },
      });
      setState('freigegeben');
      onFreigegeben(Display.Anfrage);
    } catch (exception) {
      setError(
        exception instanceof Error && exception.message
          ? exception.message.replace(/^\d{3} [^:]+: /, '')
          : 'Die Freigabe ist fehlgeschlagen.'
      );
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <Paper
      variant='outlined'
      sx={{ p: 1.5, borderColor: mitMandanten ? 'warning.main' : 'info.main' }}
    >
      <Stack spacing={1}>
        <Stack direction='row' spacing={1} alignItems='center'>
          {mitMandanten ? (
            <WarningAmber fontSize='small' color='warning' />
          ) : (
            <Language fontSize='small' color='info' />
          )}
          <Typography variant='subtitle2'>
            {mitMandanten
              ? 'Internetsuche mit Mandantendaten'
              : 'Im Internet suchen?'}
          </Typography>
        </Stack>
        <Typography variant='body2'>
          Karl möchte im Internet suchen: <strong>„{Display.Anfrage}"</strong>
        </Typography>
        {mitMandanten && (
          <Alert severity='warning' sx={{ py: 0 }}>
            Die Anfrage enthält Mandantendaten:{' '}
            <strong>{Display.Mandanten.join(', ')}</strong>.
          </Alert>
        )}
        <Typography variant='caption' color='text.secondary'>
          Gesucht wird über {suchdienst}. Die Freigabe gilt einmal, für genau
          diese Suche.
        </Typography>
        {error !== null && <Alert severity='error'>{error}</Alert>}
        <Stack direction='row' spacing={1} justifyContent='flex-end'>
          <Button
            size='small'
            color='inherit'
            disabled={isLoading}
            onClick={() => setState('abgelehnt')}
          >
            Nein, nicht suchen
          </Button>
          <Button
            size='small'
            variant='contained'
            color={mitMandanten ? 'warning' : 'primary'}
            startIcon={
              isLoading ? (
                <CircularProgress size={16} color='inherit' />
              ) : (
                <Language />
              )
            }
            disabled={isLoading}
            onClick={() => void handleFreigabe()}
          >
            Ja, im Internet suchen
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
};
